import React from 'react';
import { Wrench, Replace } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import PartLookupInput from '../ui/part-lookup-input';
import { X } from 'lucide-react';
import ZeroValueBadge from './ZeroValueBadge';

const ApproachButton = ({
  part,
  zoneId,
  onUpdatePart,
  childReplacementParts = [],
  onAddChildPart,
  onRemoveChildPart,
  onUpdateChildPart,
  insurerConfigId,
  hasInOutPaint,
  defaultsReady = false,
}) => {
  const isRepair = part.approach === 'repair';
  const isReplace = part.approach === 'replace';
  // Repair contributes panel-beating hours; flag when it's on but resolves to zero.
  const effectiveRepairHours = part.repairHoursOverride ?? part.defaultRepairHours;
  const needsAttention = defaultsReady && isRepair && part.panelbeating && !(Number(effectiveRepairHours) > 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={isRepair ? 'default' : 'default'}
          size="sm"
          className={`relative h-8 gap-1.5 text-xs text-white ${
            isRepair
              ? 'bg-chart-3 hover:bg-chart-3/90'
              : 'bg-chart-1 hover:bg-chart-1/90'
          }`}
        >
          {needsAttention && <ZeroValueBadge title="No repair hours set — this line will be blank on the quote" />}
          {isRepair ? <Wrench className="h-3.5 w-3.5" /> : <Replace className="h-3.5 w-3.5" />}
          {isRepair ? 'Repair' : 'Replace'}
          {isReplace && childReplacementParts.length > 0 && (
            <span className="ml-0.5 text-[10px] bg-white/25 rounded-full px-1.5">
              +{childReplacementParts.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="start">
        <div className="space-y-3">
          <div className="text-xs font-medium text-muted-foreground">Approach</div>
          
          {/* Approach toggle buttons */}
          <div className="flex gap-1">
            <Button
              type="button"
              variant={isRepair ? 'default' : 'outline'}
              size="sm"
              className={`flex-1 gap-1.5 text-xs ${isRepair ? 'bg-chart-3 hover:bg-chart-3/90 text-white' : ''}`}
              onClick={() => onUpdatePart(zoneId, part.id, 'approach', 'repair')}
            >
              <Wrench className="h-3.5 w-3.5" />
              Repair
            </Button>
            <Button
              type="button"
              variant={isReplace ? 'default' : 'outline'}
              size="sm"
              className={`flex-1 gap-1.5 text-xs ${isReplace ? 'bg-chart-1 hover:bg-chart-1/90 text-white' : ''}`}
              onClick={() => onUpdatePart(zoneId, part.id, 'approach', 'replace')}
            >
              <Replace className="h-3.5 w-3.5" />
              Replace
            </Button>
          </div>

          {/* Repair-specific: Panelbeating + hours */}
          {isRepair && (
            <div className="space-y-2 pt-1 border-t">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`panelbeating-pop-${part.id}`}
                  checked={part.panelbeating}
                  onCheckedChange={(checked) => onUpdatePart(zoneId, part.id, 'panelbeating', checked)}
                />
                <Label htmlFor={`panelbeating-pop-${part.id}`} className="text-xs cursor-pointer">
                  Panelbeating
                </Label>
              </div>

              {part.panelbeating && (
                <div className="space-y-1 pt-1">
                  <Label htmlFor={`repairHours-${part.id}`} className="text-xs text-muted-foreground">
                    Repair Hours
                  </Label>
                  <Input
                    id={`repairHours-${part.id}`}
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder={part.defaultRepairHours != null ? String(part.defaultRepairHours) : '0'}
                    value={part.repairHoursOverride ?? ''}
                    onChange={(e) => onUpdatePart(zoneId, part.id, 'repairHoursOverride', e.target.value ? Number(e.target.value) : null)}
                    className="h-7 text-xs"
                  />
                </div>
              )}
            </div>
          )}

          {/* Replace-specific: Child replacement parts */}
          {isReplace && childReplacementParts.length > 0 && (
            <div className="space-y-2 pt-1 border-t">
              <div className="text-xs font-medium text-muted-foreground">
                {childReplacementParts.length} replacement part{childReplacementParts.length !== 1 ? 's' : ''}
              </div>
              <div className="space-y-1.5">
                {childReplacementParts.map(childPart => (
                  <div key={childPart.id} className="flex items-center gap-1.5">
                    <PartLookupInput
                      value={childPart.part}
                      onChange={(partName) => onUpdateChildPart(zoneId, childPart.id, 'part', partName)}
                      insurerConfigId={insurerConfigId}
                      zone={zoneId}
                      placeholder="Part name"
                      disabled={!insurerConfigId}
                      className="mb-0 flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => onRemoveChildPart(zoneId, childPart.id)}
                      className="p-1 text-muted-foreground hover:text-destructive flex-shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              <PartLookupInput
                value=""
                onChange={(partName) => {
                  if (partName && partName.trim()) {
                    onAddChildPart(zoneId, part, partName);
                  }
                }}
                insurerConfigId={insurerConfigId}
                zone={zoneId}
                placeholder="Add replacement part..."
                disabled={!insurerConfigId}
                commitOnBlur={false}
                clearOnChange={true}
                className="mb-0"
              />
            </div>
          )}

          {isReplace && childReplacementParts.length === 0 && (
            <div className="pt-1 border-t">
              <div className="text-xs text-muted-foreground mb-1.5">Replacement parts</div>
              <PartLookupInput
                value=""
                onChange={(partName) => {
                  if (partName && partName.trim()) {
                    onAddChildPart(zoneId, part, partName);
                  }
                }}
                insurerConfigId={insurerConfigId}
                zone={zoneId}
                placeholder="Add replacement part..."
                disabled={!insurerConfigId}
                commitOnBlur={false}
                clearOnChange={true}
                className="mb-0"
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ApproachButton;
